/**
 * Recurring signup sheet rules (plan 2026-09-18-0147, U1 / KTD1, KTD2).
 *
 * A signup sheet (`TicketedEvent.kind = signup`) becomes recurring by carrying
 * two JSON rules: a REPEAT rule saying which dated slots to mint, and a CLOSE
 * rule yielding one cutoff per minted slot. Both live as `Json` columns on
 * `TicketedEvent` (KTD1) — nothing ever queries inside a rule, every read of a
 * sheet already loads the row, and a one-to-one rule table would be a join for
 * no consumer. These schemas are therefore the ONLY validation the columns get.
 *
 * Times and dates in a rule are SCHOOL-LOCAL wall-clock strings, never
 * instants: `HH:mm` and `YYYY-MM-DD`. A rule says "every weekday at 3:15 PM",
 * which stays 3:15 PM across a daylight-saving change; storing an instant would
 * shift it by an hour. The generator resolves them against the school's
 * timezone (KTD4) — a school with no timezone on file cannot publish a
 * recurring sheet (R6).
 */

import { z } from 'zod'

/**
 * The bounds a rule is validated against, and the defaults the organizer's form
 * starts from. Constants rather than columns: they are product limits, so
 * changing one is a deploy, not a migration (Assumptions).
 *
 * `MAX_GENERATED_SLOTS` is the whole-sheet ceiling the generator enforces
 * (weekdays x windows x dates can multiply well past any single field's
 * maximum); it is not reachable from this file's per-field checks.
 */
export const SIGNUP_RULE_LIMITS = {
  /** At most four time windows on any one date. */
  MAX_WINDOWS_PER_DATE: 4,
  /** The furthest ahead a sheet may show dates to registrants. */
  MAX_HORIZON_DAYS: 60,
  /** The largest count-based end a rule may declare. */
  MAX_COUNT: 400,
  /** Whole-sheet ceiling on minted slots, enforced by the generator. */
  MAX_GENERATED_SLOTS: 1000,
  /**
   * Horizon applied when the organizer sets none. A display bound on what a
   * registrant sees (SCH-2021) — the sheet's slots all exist from the moment
   * the rule is saved, so this only decides how many of them are shown.
   * Sixty days is what signup planners expect (SignUp.com advises planning
   * three to five months ahead).
   */
  DEFAULT_HORIZON_DAYS: 60,
  /** Spots a generated slot carries when the rule names no count (R5). */
  DEFAULT_SPOTS: 20,
  /** Spots one registrant email may hold across the sheet's horizon (R9). */
  DEFAULT_CAP: 10
} as const

/**
 * A school-local wall-clock time, `HH:mm` on a 24-hour clock, zero-padded.
 *
 * Zero-padding is not cosmetic: the padded form sorts and compares
 * lexicographically, which is how a window's end is checked against its start
 * and how the generated key (`20261126T1515`, KTD2) stays stable.
 */
const LocalTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Expected a local time as HH:mm')

/** A school-local calendar date, `YYYY-MM-DD`. */
const LocalDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a local date as YYYY-MM-DD')

/**
 * One time window on a date. Start and end are on the SAME calendar day (R1),
 * which the string comparison below enforces: an overnight window would need a
 * second date and has no meaning on a signup sheet.
 */
export const SignupTimeWindowSchema = z
  .object({
    start: LocalTimeSchema,
    end: LocalTimeSchema
  })
  .refine(window => window.end > window.start, {
    message: 'A window must end after it starts, on the same day',
    path: ['end']
  })
export type SignupTimeWindow = z.infer<typeof SignupTimeWindowSchema>

/**
 * Where the series stops: a last date, or a number of generated dates.
 *
 * A count materializes to a last date at save (KTD4) — it is the organizer's
 * way of saying "eight sessions", and a later exclusion keeps the count and
 * moves the last date rather than dropping a session.
 */
export const SignupRepeatEndSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('date'), date: LocalDateSchema }),
  z.object({ kind: z.literal('count'), count: z.number().int().min(1).max(SIGNUP_RULE_LIMITS.MAX_COUNT) })
])
export type SignupRepeatEnd = z.infer<typeof SignupRepeatEndSchema>

/**
 * The repeat rule: which dated slots a sheet mints (R1, R2).
 *
 * `weekdays` are JavaScript's own day numbers (0 = Sunday ... 6 = Saturday), so
 * the generator compares against `Date.getDay()` with no lookup table.
 */
export const SignupRepeatRuleSchema = z.object({
  weekdays: z.array(z.number().int().min(0).max(6)).min(1, 'A rule must run on at least one weekday'),
  windows: z
    .array(SignupTimeWindowSchema)
    .min(1, 'A rule needs at least one time window')
    .max(SIGNUP_RULE_LIMITS.MAX_WINDOWS_PER_DATE)
    // Distinct starts, because a slot's generated key is its local date plus
    // its start time (KTD2). Two windows starting together would generate one
    // key twice and the mint — which is idempotent ON that key — would keep
    // only one of them, silently losing a slot the organizer asked for.
    .refine(windows => new Set(windows.map(window => window.start)).size === windows.length, {
      message: 'Two windows on the same date cannot start at the same time'
    }),
  firstDate: LocalDateSchema,
  end: SignupRepeatEndSchema,
  /**
   * Dates that are never minted — holidays, breaks, a snow day (R1). Defaulted
   * rather than required so a rule with no exclusions can omit the key; the
   * generator always reads an array.
   */
  exclusionDates: z.array(LocalDateSchema).default([]),
  /**
   * How far ahead dates show to registrants (R2). Optional — a rule that names
   * no horizon takes `DEFAULT_HORIZON_DAYS`. The materialized value lives on
   * `TicketedEvent.horizonDays`; this is the organizer's setting as saved with
   * the rule.
   */
  horizonDays: z.number().int().min(1).max(SIGNUP_RULE_LIMITS.MAX_HORIZON_DAYS).optional(),
  /**
   * Spots every generated slot starts with (R5). Optional for the same reason
   * `horizonDays` is: a rule that names none takes `DEFAULT_SPOTS`, and making
   * it required would retro-invalidate rules written against the U1 schema.
   *
   * It is the slot's DEFAULT, not a running limit — once a slot exists an
   * organizer can change its own count, and that edit stamps the slot out of
   * rule propagation (KTD11). Lowering this only moves slots that carry no
   * registrations and no stamp (AE4).
   */
  defaultSpots: z.number().int().min(1).max(100_000).optional()
})
export type SignupRepeatRule = z.infer<typeof SignupRepeatRuleSchema>

/**
 * The close rule: one cutoff per slot (R6), in either of two forms an organizer
 * actually asks for — "sign-ups close at 1 PM that day", or "sign-ups close an
 * hour before the shift".
 *
 * `minutes` allows 0: closing exactly at the slot's start is a real choice, and
 * the gate is a strict comparison against the cutoff, so a zero offset still
 * refuses a registration that lands after the slot has begun.
 */
export const SignupCloseRuleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('time_of_day'), time: LocalTimeSchema }),
  z.object({ kind: z.literal('minutes_before'), minutes: z.number().int().min(0) })
])
export type SignupCloseRule = z.infer<typeof SignupCloseRuleSchema>

/**
 * How one slot reads to a registrant or organizer, DERIVED at read time from
 * the slot's cutoff, cancellation stamp, start time and remaining capacity
 * (KTD2). Nothing flips a status on a clock, so there is no column behind this
 * and no job that can leave a slot in a stale state.
 */
export const SlotStateSchema = z.enum(['open', 'closed', 'filled', 'cancelled', 'past'])
export type SlotState = z.infer<typeof SlotStateSchema>

/**
 * Who a registration is FOR, distinct from who placed it (R10).
 *
 * `registrant` — a volunteer sheet: the adult signs themselves up.
 * `child` — an enrollment sheet: the parent names a child per spot. In this
 * round the child is a name the registrant types; linking it to a real student
 * waits for the native claim spine.
 *
 * Null on the event means the sheet declares no participant, which is what
 * keeps today's live signup events on the existing per-email rule (KTD7).
 */
export const ParticipantKindSchema = z.enum(['registrant', 'child'])
export type ParticipantKind = z.infer<typeof ParticipantKindSchema>
